import {
    fetchPost,
    fetchPostFormData,
} from "../../../../../../../../../../common/util/fetch";
import { checkUser } from "../../../../../../../../../../common/util/User/checkUser";
import { FetchResult } from "../../../../../../../../../../types/fetch";
import { shurikenProgressKey } from "../../../../../../../../Markdown/ImageRender/SocialPostImage";
import { User, UserForRanking } from "../../../../../../../../User/types";
import { Post, PostComment } from "../../../../../types";
import { SocialInputReturn } from "../InputFieldArea";

async function fetchLikeComment(
    userId: number,
    postNo: number,
    postedUserId: number,
    replyTargetUserId: number,
    replyTargetCommentNo: number
): Promise<
    FetchResult<{
        comments: PostComment[];
        commentedUsers: UserForRanking[];
    }>
> {
    return fetchPost<
        {
            userId: number;
            postNo: number;
            postedUserId: number;
            replyTargetUserId: number;
            replyTargetCommentNo: number;
        },
        {
            comments: PostComment[];
            commentedUsers: UserForRanking[];
        }
    >("api/PostComment/LikeComment", {
        userId,
        postNo,
        postedUserId,
        replyTargetUserId,
        replyTargetCommentNo,
    });
}

async function fetchDeleteComment(
    userId: number,
    postedUserId: number,
    postNo: number,
    commentNo: number
): Promise<void> {
    await fetchPost<
        {
            userId: number;
            postedUserId: number;
            postNo: number;
            commentNo: number;
        },
        {
            comments: PostComment[];
            commentedUsers: UserForRanking[];
        }
    >("api/PostComment/DeleteComment", {
        userId,
        postedUserId,
        postNo,
        commentNo,
    });
}

async function fetchUnlikeComment(
    userId: number,
    postedUserId: number,
    postNo: number,
    commentNo: number
): Promise<void> {
    await fetchPost<
        {
            userId: number;
            postedUserId: number;
            postNo: number;
            commentNo: number;
        },
        {
            comments: PostComment[];
            commentedUsers: UserForRanking[];
        }
    >("api/PostComment/UnlikeComment", {
        userId,
        postedUserId,
        postNo,
        commentNo,
    });
}

function getTmpCommentLike(post: Post, user: User, comment: PostComment) {
    return {
        postedUserId: post.userId,
        postNo: post.postNo,
        commentNo: 0,
        commentedUserId: user.userId,
        minutesAgo: 0,
        text: "",
        isLike: true,
        isRead: false,
        replyTargetUserId: comment.commentedUserId,
        replyTargetCommentNo: comment.commentNo,
        isReplyTargetRead: false,
        deleted: false,
    };
}

export const getDeleteComment =
    (
        post: Post,
        comment: PostComment,
        deleteComment: (
            targetComment: PostComment,
            deleteFromServerSie: () => Promise<void>
        ) => Promise<void>
    ) =>
    () => {
        const user = checkUser();
        if (!user) {
            return;
        }
        if (confirm("This comment will be deleted.\nAre you sure?")) {
            deleteComment(comment, async () => {
                await fetchDeleteComment(
                    user.userId,
                    post.userId,
                    post.postNo,
                    comment.commentNo
                );
            });
        }
    };

export const getLikeComment =
    (
        post: Post,
        comment: PostComment,
        comments: PostComment[],
        setComments: (comments: PostComment[]) => void,
        setCommentedUsers: (users: UserForRanking[]) => void,
        showReply: () => void
    ) =>
    () => {
        const user = checkUser();
        if (!user) {
            return;
        }
        showReply();
        setComments([getTmpCommentLike(post, user, comment), ...comments]);

        fetchLikeComment(
            user.userId,
            post.postNo,
            post.userId,
            comment.commentedUserId,
            comment.commentNo
        ).then(r => {
            if ("error" in r) {
                return;
            }
            setComments(r.comments);
            setCommentedUsers(r.commentedUsers);
        });
    };

export const getUnlikeComment =
    (
        post: Post,
        comment: PostComment,
        comments: PostComment[],
        deleteComment: (
            targetComment: PostComment,
            deleteFromServerSie: () => Promise<void>
        ) => Promise<void>
    ) =>
    () => {
        const user = checkUser();
        if (!user) {
            return;
        }
        const targetComment = comments.find(
            com =>
                com.replyTargetCommentNo === comment.commentNo &&
                com.isLike &&
                com.commentedUserId === user.userId
        );
        if (!targetComment || targetComment.commentNo < 1) {
            return;
        }
        deleteComment(targetComment, async () => {
            await fetchUnlikeComment(
                user.userId,
                post.userId,
                post.postNo,
                targetComment.commentNo
            );
        });
    };

export const checkUnreadComment = (
    com: PostComment,
    unreadCommentNos: number[],
    comments: PostComment[]
): boolean => {
    if (unreadCommentNos.length < 1) {
        return false;
    }
    if (unreadCommentNos.includes(com.commentNo)) {
        return true;
    }
    return comments.some(
        co =>
            co.replyTargetCommentNo === com.commentNo &&
            checkUnreadComment(co, unreadCommentNos, comments)
    );
};

export function getTmpComment(
    post: Post,
    commentedUserId: number,
    text: string,
    replyTargetUserId: number | null = null,
    replyTargetCommentNo: number | null = null,
    hasFile: boolean
): PostComment {
    return {
        postedUserId: post.userId,
        postNo: post.postNo,
        commentNo: 0,
        commentedUserId,
        minutesAgo: 0,
        text: hasFile ? `${text}![shuriken](${shurikenProgressKey})` : text,
        isLike: false,
        isRead: false,
        replyTargetUserId,
        replyTargetCommentNo,
        isReplyTargetRead: false,
        deleted: false,
    };
}

export async function registerImageComment(
    userId: number,
    postedUserId: number,
    postNo: number,
    text: string,
    files: File[],
    replyTargetUserId: number = 0,
    replyTargetCommentNo: number = 0
) {
    const formData = new FormData();
    formData.append("userId", userId.toString());
    formData.append("postedUserId", postedUserId.toString());
    formData.append("postNo", postNo.toString());
    formData.append("text", text);
    for (let file of files) {
        formData.append("files", file);
    }
    formData.append("replyTargetUserId", replyTargetUserId.toString());
    formData.append("replyTargetCommentNo", replyTargetCommentNo.toString());

    await fetchPostFormData("api/PostComment/AddImageComment", formData);
}

export async function fetchCommentToPost(
    userId: number,
    postedUserId: number,
    postNo: number,
    text: string
): Promise<void> {
    await fetchPost<
        {
            userId: number;
            postedUserId: number;
            postNo: number;
            text: string;
        },
        undefined
    >("api/PostComment/AddCommentToPost", {
        userId,
        postedUserId,
        postNo,
        text,
    });
}

export async function fetchCommentToComment(
    userId: number,
    postedUserId: number,
    postNo: number,
    text: string,
    replyTargetUserId: number = 0,
    replyTargetCommentNo: number = 0
): Promise<void> {
    await fetchPost<
        {
            userId: number;
            postedUserId: number;
            postNo: number;
            text: string;
            replyTargetUserId: number;
            replyTargetCommentNo: number;
        },
        undefined
    >("api/PostComment/AddCommentToComment", {
        userId,
        postedUserId,
        postNo,
        text,
        replyTargetUserId,
        replyTargetCommentNo,
    });
}

export const getSendText =
    (
        addTmpComment: (tmpComment: PostComment) => void,
        post: Post,
        addCommentCount: (count: number) => void,
        replyTargetComment?: PostComment
    ) =>
    async (text: string, files: File[]): Promise<SocialInputReturn> => {
        const user = checkUser();
        if (!user) {
            return "shouldNotClearField";
        }

        const hasFile = files.length > 0;

        addTmpComment(
            getTmpComment(
                post,
                user.userId,
                text,
                replyTargetComment?.commentedUserId,
                replyTargetComment?.commentNo,
                hasFile
            )
        );

        if (hasFile) {
            await registerImageComment(
                user.userId,
                post.userId,
                post.postNo,
                text,
                files,
                replyTargetComment?.commentedUserId,
                replyTargetComment?.commentNo
            );
        } else {
            if (replyTargetComment) {
                await fetchCommentToComment(
                    user.userId,
                    post.userId,
                    post.postNo,
                    text,
                    replyTargetComment.commentedUserId,
                    replyTargetComment.commentNo
                );
            } else {
                await fetchCommentToPost(
                    user.userId,
                    post.userId,
                    post.postNo,
                    text
                );
            }
        }
        addCommentCount(1);
    };
